import { queryKeys } from '../query/queryKeys';
import { resolveRosterPeerId } from './engine/peerId';

/** Matches standard UUID strings — never show these as human-readable names. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const FIND_PEER_FALLBACK_LABEL = 'Group member';

export function isUuidLikeString(value) {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 && UUID_RE.test(trimmed);
}

function readCachedProfileName(queryClient, peerId) {
  if (!queryClient || !peerId) {
    return null;
  }
  for (const mode of ['public', 'own']) {
    const bundle = queryClient.getQueryData(queryKeys.profiles.detail(peerId, mode));
    const name = bundle?.profile?.display_name;
    if (typeof name === 'string' && name.trim() && !isUuidLikeString(name)) {
      return name.trim();
    }
  }
  return null;
}

function readCachedProfilePhoto(queryClient, peerId) {
  if (!queryClient || !peerId) {
    return null;
  }
  for (const mode of ['public', 'own']) {
    const bundle = queryClient.getQueryData(queryKeys.profiles.detail(peerId, mode));
    const url = bundle?.profile?.profile_photo_url;
    if (typeof url === 'string' && url.trim()) {
      return url.trim();
    }
  }
  return null;
}

/**
 * The Find page's own member list (['find', 'members', ...], fetchMembersForGroups) already holds every
 * group member's name and photo. A peer whose profile fetch missed, or who is filed under a cut-short
 * Bluetooth id, is named from it by full id or unique prefix (2026-10-14 Turnstile: both phones read
 * "Group member").
 */
function readRosterMember(queryClient, peerId) {
  if (!queryClient || !peerId || typeof queryClient.getQueriesData !== 'function') {
    return null;
  }
  const byId = new Map();
  for (const [, data] of queryClient.getQueriesData({ queryKey: ['find', 'members'] })) {
    for (const member of Array.isArray(data) ? data : []) {
      if (member?.userId && !byId.has(member.userId)) {
        byId.set(member.userId, member);
      }
    }
  }
  if (byId.size === 0) {
    return null;
  }
  const userId = resolveRosterPeerId(String(peerId), [...byId.keys()]);
  return userId ? byId.get(userId) : null;
}

function readRosterName(queryClient, peerId) {
  const name = readRosterMember(queryClient, peerId)?.displayName;
  if (
    typeof name === 'string' &&
    name.trim() &&
    name.trim() !== FIND_PEER_FALLBACK_LABEL &&
    !isUuidLikeString(name)
  ) {
    return name.trim();
  }
  return null;
}

function readRosterPhoto(queryClient, peerId) {
  const url = readRosterMember(queryClient, peerId)?.photoUrl;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
}

function readSelfProfileName(selfProfile) {
  const name = selfProfile?.display_name;
  if (typeof name === 'string' && name.trim() && !isUuidLikeString(name)) {
    return name.trim();
  }
  return null;
}

function readSelfProfilePhoto(selfProfile) {
  const url = selfProfile?.profile_photo_url;
  if (typeof url === 'string' && url.trim()) {
    return url.trim();
  }
  return null;
}

function sanitizeSessionName(name) {
  if (typeof name !== 'string') {
    return null;
  }
  const trimmed = name.trim();
  if (!trimmed || isUuidLikeString(trimmed)) {
    return null;
  }
  return trimmed;
}

/**
 * Resolve a peer display name for Find — never returns a UUID-shaped string.
 */
export function resolveFindPeerDisplayName({
  peerId,
  queryClient = null,
  viewerUserId = null,
  selfProfile = null,
  sessionDisplayName = null,
} = {}) {
  const fromSession = sanitizeSessionName(sessionDisplayName);
  if (fromSession) {
    return fromSession;
  }

  const cached = readCachedProfileName(queryClient, peerId);
  if (cached) {
    return cached;
  }

  if (viewerUserId && peerId === viewerUserId) {
    const selfName = readSelfProfileName(selfProfile);
    if (selfName) {
      return selfName;
    }
  }

  const fromRoster = readRosterName(queryClient, peerId);
  if (fromRoster) {
    return fromRoster;
  }

  return FIND_PEER_FALLBACK_LABEL;
}

export function resolveFindPeerPhotoUrl({
  peerId,
  queryClient = null,
  viewerUserId = null,
  selfProfile = null,
  sessionPhotoUrl = null,
} = {}) {
  if (typeof sessionPhotoUrl === 'string' && sessionPhotoUrl.trim()) {
    return sessionPhotoUrl.trim();
  }

  const cached = readCachedProfilePhoto(queryClient, peerId);
  if (cached) {
    return cached;
  }

  if (viewerUserId && peerId === viewerUserId) {
    return readSelfProfilePhoto(selfProfile);
  }

  return readRosterPhoto(queryClient, peerId);
}

/** Initial letter for avatar fallbacks — never derived from a UUID. */
export function findPeerInitial(displayName) {
  const trimmed = (displayName ?? '').trim();
  if (!trimmed || isUuidLikeString(trimmed)) {
    return '?';
  }
  return trimmed.charAt(0).toUpperCase();
}

export function sanitizeSnapshotDisplayName(name) {
  return sanitizeSessionName(name);
}
